import { useEffect, useState } from 'react';
import type { AdminListItem } from '../../lib/panelApi';
import { createAdmin, deleteAdmin, listAdmins, updateAdmin } from '../../lib/panelApi';

type Props = {
  accessToken: string;
  currentAdminUuid: string;
};

// Ordered from least privileged to most. We intentionally do NOT
// expose `viewer` here: in the current backend the role would behave
// identically to `editor` (no enforced read-only on POST/PUT/DELETE),
// which is misleading. If a true read-only role is needed later we
// will add it back together with the matching guards.
const roles = ['editor', 'admin', 'superadmin'] as const;

export function AdminsPage({ accessToken, currentAdminUuid }: Props) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<AdminListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  // Default to the lowest-privilege role so an inattentive click on
  // "Create" doesn't accidentally mint a superadmin.
  const [createRole, setCreateRole] = useState<(typeof roles)[number]>('editor');
  const [creating, setCreating] = useState(false);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<(typeof roles)[number]>('editor');
  const [editPassword, setEditPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

  async function refresh(query: string) {
    setLoading(true);
    setError(null);
    try {
      const res = await listAdmins(accessToken, query, 200, 0);
      setItems(res.items);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load admins';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  async function onCreate() {
    const email = createEmail.trim().toLowerCase();
    if (!email || !createPassword) {
      setError('Email and password are required to create an admin.');
      return;
    }
    if (createPassword.length < 10) {
      setError('Password must be at least 10 characters.');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const res = await createAdmin(accessToken, { email, password: createPassword, role: createRole });
      setItems((prev) => [res, ...prev]);
      setCreateEmail('');
      setCreatePassword('');
      setCreateRole('admin');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Create failed';
      setError(msg);
    } finally {
      setCreating(false);
    }
  }

  async function onSave(admin: AdminListItem) {
    const payload: { role?: string; password?: string } = {};
    if (editRole && editRole !== admin.role) payload.role = editRole;
    if (editPassword.trim()) payload.password = editPassword;
    if (!payload.role && !payload.password) {
      setError('No changes to save.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await updateAdmin(accessToken, admin.uuid, payload);
      setItems((prev) => prev.map((x) => (x.uuid === res.uuid ? res : x)));
      setEditPassword('');
      setExpanded(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Update failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(admin: AdminListItem) {
    if (admin.uuid === currentAdminUuid) {
      setError('You cannot delete your own admin account.');
      return;
    }
    const ok = confirm(`Delete admin "${admin.email}"? This cannot be undone.`);
    if (!ok) return;

    setDeletingUuid(admin.uuid);
    setError(null);
    try {
      await deleteAdmin(accessToken, admin.uuid);
      setItems((prev) => prev.filter((x) => x.uuid !== admin.uuid));
      if (expanded === admin.uuid) setExpanded(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      setError(msg);
    } finally {
      setDeletingUuid(null);
    }
  }

  return (
    <div className="card">
      <div className="header" style={{ marginBottom: 8 }}>
        <div>
          <div className="sectionTitle" style={{ margin: 0 }}>
            Admins
          </div>
          <div className="subtitle">Panel users and role management.</div>
        </div>
        <button className="btn" onClick={() => refresh(q.trim())} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="item" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 850 }}>Create admin</div>
        <div className="row" style={{ marginTop: 10, alignItems: 'end' }}>
          <div className="field" style={{ marginTop: 0, flex: 1, minWidth: 220 }}>
            <div className="label">Email</div>
            <input value={createEmail} onChange={(e) => setCreateEmail(e.target.value)} placeholder="admin@example.com" />
          </div>
          <div className="field" style={{ marginTop: 0, flex: 1, minWidth: 220 }}>
            <div className="label">Password</div>
            <input
              value={createPassword}
              onChange={(e) => setCreatePassword(e.target.value)}
              placeholder="Min 10 chars"
              type="password"
            />
          </div>
          <div className="field" style={{ marginTop: 0, minWidth: 170 }}>
            <div className="label">Role</div>
            <select value={createRole} onChange={(e) => setCreateRole(e.target.value as any)}>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={onCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
        <div className="muted" style={{ marginTop: 10, fontSize: 12, lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--accent)' }}>editor</strong>: content only (books, taxonomy, uploads).
          {' · '}
          <strong style={{ color: 'var(--accent)' }}>admin</strong>: content + users + reports.
          {' · '}
          <strong style={{ color: 'var(--accent)' }}>superadmin</strong>: everything, including admin management.
        </div>
      </div>

      <div className="row" style={{ marginBottom: 10, alignItems: 'end' }}>
        <div className="field" style={{ marginTop: 0, flex: 1, minWidth: 260 }}>
          <div className="label">Search</div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email…" />
        </div>
        <button className="btn btn-secondary" onClick={() => refresh(q.trim())} disabled={loading}>
          Search
        </button>
        <button
          className="btn"
          onClick={() => {
            setQ('');
            void refresh('');
          }}
          disabled={loading}
        >
          Clear
        </button>
      </div>

      {error ? (
        <div style={{ marginBottom: 10 }}>
          <div className="errorBox">{error}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="muted">Loading admins…</div>
      ) : items.length === 0 ? (
        <div className="muted">No admins found.</div>
      ) : (
        <div className="list">
          {items.map((a) => {
            const isExpanded = expanded === a.uuid;
            const isSelf = a.uuid === currentAdminUuid;
            return (
              <div className="item" key={a.uuid}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
                  <div>
                    <div style={{ fontWeight: 900 }}>{a.email}</div>
                    <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                      Role: <span style={{ color: 'var(--accent)' }}>{a.role}</span>
                      {' • '}
                      {new Date(a.created_at).toLocaleString()}
                      {isSelf ? ' • (you)' : ''}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        if (!isExpanded) {
                          setEditRole((a.role as any) || 'admin');
                          setEditPassword('');
                          setExpanded(a.uuid);
                        } else {
                          setExpanded(null);
                        }
                      }}
                    >
                      {isExpanded ? 'Close' : 'Edit'}
                    </button>
                    <button className="btn btn-danger" onClick={() => onDelete(a)} disabled={deletingUuid === a.uuid || isSelf}>
                      {isSelf ? 'Locked' : deletingUuid === a.uuid ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>

                {isExpanded ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="row" style={{ alignItems: 'end' }}>
                      <div className="field" style={{ marginTop: 0, minWidth: 170 }}>
                        <div className="label">Role</div>
                        <select value={editRole} onChange={(e) => setEditRole(e.target.value as any)}>
                          {roles.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field" style={{ marginTop: 0, flex: 1, minWidth: 220 }}>
                        <div className="label">New password (optional)</div>
                        <input
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                          placeholder="Leave empty to keep current"
                          type="password"
                        />
                      </div>
                      <button className="btn btn-primary" onClick={() => onSave(a)} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                    <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                      Password must be at least 10 characters.
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

